import React, { createContext, useState, useReducer, useContext, useEffect, useRef } from 'react';
import { deriveSystemState, SYSTEM_FLAGS_ACTION } from './systemFlagsReducer';
import pollingService from '../services/pollingService';
import keycloakService from '../services/keycloakService';
import { CommandApi, ApiClient, ManualOverrideCommandRequest, CollaborativeCommand } from '../generated/src';
import { useFireController } from './FireControllerContext';
import { 
  FaFire, 
  FaSmog, 
  FaSearch, 
  FaTint, 
  FaSignal,
  FaThermometerHalf,
  FaTachometerAlt,
  FaWater,
  FaCog,
} from 'react-icons/fa';
import { MdOutlinePower } from 'react-icons/md';
import {
  SYSTEM_MODE,
  SHARED_MODE,
  SPECIAL_MODE,
  CONNECTION_STATUS,
  COMPONENT_STATUS,
  HEALTH_STATUS,
  SENSOR_ID_MAP,
  SENSOR_TYPES,
  CSS_STATUS_CLASS
} from './enums';
import { getDeactivationDuration, DEACTIVATION_TYPE, DEACTIVATION_DURATION_SHORT_PRESOAK } from './overrideDurationUtils';
import { MILISECONDS_TO_SECONDS, STALENESS_THRESHOLDS } from '../utils/timeConstants';
import { getSensorName, getRelayName } from '../utils/deviceNameUtils';

/**
 * Checks if a timeReported value is stale (older than the staleness threshold).
 * @param {string|Date} timeReported - The timeReported timestamp to check
 * @returns {boolean} True if the data is stale, false if fresh or no timestamp provided
 */
const isStaleData = (timeReported) => {
  if (!timeReported) return false;
  const elapsedSeconds = Math.floor((Date.now() - new Date(timeReported).getTime()) * MILISECONDS_TO_SECONDS);
  return elapsedSeconds >= STALENESS_THRESHOLDS.STALE_SECONDS;
};

// Time constants for command processing
const COMMAND_TIMEOUT_MS = 15000; // 15 seconds - timeout if backend never responds
const MANUAL_RESET_STATE = 'NOT_ACTIVE';
const MANUAL_RESET_DURATION = 'PT0S';
const MANUAL_RESET_DELAY = 'PT0S';
const WATER_GOOD_HOURS = 2;
const WATER_WARNING_HOURS = 0.5;
const GOOD_SIGNAL_STATUSES = ['Strong', 'Moderate'];
const WARNING_SIGNAL_STATUS = 'Weak';

// Error messages
const ERROR_MESSAGES = {
  COMMAND_API_NOT_INITIALIZED: 'CommandApi not initialized'
};

const SystemContext = createContext();

/**
 * Derives connection state from a ControlByWeb status without mutating React state.
 * @param {Object} controlData - ControlByWeb status payload
 * @param {boolean} stale - Whether the containing poll response is stale
 * @returns {{isConnected: boolean, connectionStatus: string|null}} Derived connection state
 */
function deriveControlConnectionState(controlData, stale) {
  if (!controlData.connection) {
    return { isConnected: true, connectionStatus: null };
  }

  const connectionState = controlData.connection.toLowerCase();
  if (connectionState === CONNECTION_STATUS.DISCONNECTED) {
    return { isConnected: false, connectionStatus: CONNECTION_STATUS.DISCONNECTED };
  }
  if (connectionState === CONNECTION_STATUS.CONNECTED && !stale) {
    return { isConnected: true, connectionStatus: CONNECTION_STATUS.CONNECTED };
  }
  return { isConnected: true, connectionStatus: null };
}

/**
 * Derives health for a connected water sensor from its remaining-hours status.
 * @param {string} status - Sensor status containing remaining hours
 * @returns {string} A HEALTH_STATUS value
 */
function deriveWaterSensorHealth(status) {
  const hoursMatch = status?.match(/([\d.]+)/);
  if (!hoursMatch) return HEALTH_STATUS.GOOD;

  const hours = parseFloat(hoursMatch[1]);
  if (hours >= WATER_GOOD_HOURS) return HEALTH_STATUS.GOOD;
  if (hours >= WATER_WARNING_HOURS) return HEALTH_STATUS.WARNING;
  return HEALTH_STATUS.ERROR;
}

/**
 * Derives health for a connected signal sensor from its signal-strength status.
 * @param {string} status - Signal-strength status
 * @returns {string} A HEALTH_STATUS value
 */
function deriveSignalSensorHealth(status) {
  if (GOOD_SIGNAL_STATUSES.includes(status)) return HEALTH_STATUS.GOOD;
  if (status === WARNING_SIGNAL_STATUS) return HEALTH_STATUS.WARNING;
  return HEALTH_STATUS.ERROR;
}

/**
 * Derives health for a sensor whose connection is confirmed healthy.
 * @param {string} sensorType - Logical sensor type
 * @param {Object} sensor - Sensor payload
 * @returns {string} A HEALTH_STATUS value
 */
function deriveConnectedSensorHealth(sensorType, sensor) {
  if (sensor.status === COMPONENT_STATUS.INACTIVE) return HEALTH_STATUS.ERROR;
  if (sensor.status === COMPONENT_STATUS.STANDBY) return HEALTH_STATUS.GOOD;
  if (sensorType === SENSOR_TYPES.WATER) return deriveWaterSensorHealth(sensor.status);
  if (sensorType === SENSOR_TYPES.SIGNAL) return deriveSignalSensorHealth(sensor.status);
  return HEALTH_STATUS.GOOD;
}

/**
 * Derives sensor health from its connection and domain-specific status.
 * @param {string} sensorType - Logical sensor type
 * @param {Object|undefined} sensor - Matching sensor payload
 * @returns {string} A HEALTH_STATUS value
 */
function deriveSensorHealth(sensorType, sensor) {
  if (!sensor?.connection) return HEALTH_STATUS.UNKNOWN;

  const connection = sensor.connection.toLowerCase();
  if (connection === CONNECTION_STATUS.DISCONNECTED || connection === CONNECTION_STATUS.ERROR) {
    return HEALTH_STATUS.ERROR;
  }
  if (connection !== CONNECTION_STATUS.CONNECTED) return HEALTH_STATUS.UNKNOWN;
  return deriveConnectedSensorHealth(sensorType, sensor);
}

/**
 * SystemProvider component - Provides global system state and control functions
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} The context provider wrapper component
 */
export const SystemProvider = ({ children }) => {
  const { selectedFireControllerId } = useFireController();

  const [systemData, setSystemData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.DISCONNECTED);
  const [lastError, setLastError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [systemStatus, setSystemStatus] = useState(null);
  const [systemFlagsState, dispatch] = useReducer(deriveSystemState, { isPresoaking: false, isActive: false, isCollaborative: false });
  const { isPresoaking, isActive, isCollaborative } = systemFlagsState;
  const [activeTime] = useState(30);
  const [isTestMode, setIsTestMode] = useState(false);
  const [timeReported, setTimeReported] = useState(null);

  const [sensors, setSensors] = useState([]);
  const [relays, setRelays] = useState([]);
  const [externalSensors, setExternalSensors] = useState([]);
  const [systemStatusDuration, setSystemStatusDuration] = useState(null);

  const isInitialized = useRef(false);
  const ipAddress = useRef(null);
  const commandApi = useRef(null);
  const previousSystemStatus = useRef(null);
  const pendingCommandTimestamp = useRef(null);
  const pendingCommandFireControllerId = useRef(null);
  const pendingCommandTimeout = useRef(null);
  const tokenGetter = useRef(null);

  /**
   * Applies the pending-command polling window for the selected Fire Controller.
   * @param {Object} data - Incoming poll response
   * @returns {boolean} True when the response must be ignored
   */
  const shouldIgnorePendingCommandPoll = (data) => {
    const commandTimestamp = pendingCommandTimestamp.current;
    if (
      !commandTimestamp ||
      pendingCommandFireControllerId.current !== selectedFireControllerId ||
      !data.timeSystemStatusLastUpdate
    ) {
      return false;
    }

    const pollDataTimestamp = data.timeSystemStatusLastUpdate;
    const processingDeadline = new Date(commandTimestamp.getTime());
    if (pollDataTimestamp < processingDeadline) {
      console.log('Ignoring poll data during Fire Controller processing window:');
      console.log('  Command sent at:       ', commandTimestamp.toISOString());
      console.log('  Processing deadline:   ', processingDeadline.toISOString());
      console.log('  Poll data updated at:  ', pollDataTimestamp.toISOString());
      console.log('  Time until deadline:   ', (processingDeadline - pollDataTimestamp), 'ms');
      return true;
    }

    console.log('Processing window complete - accepting backend state:');
    console.log('  Command sent at:      ', commandTimestamp.toISOString());
    console.log('  Processing deadline:  ', processingDeadline.toISOString());
    console.log('  Poll data updated at: ', pollDataTimestamp.toISOString());
    console.log('  Total processing time:', (pollDataTimestamp - commandTimestamp), 'ms');
    console.log('  Backend status:       ', data.systemStatus);
    pendingCommandTimestamp.current = null;
    pendingCommandFireControllerId.current = null;
    clearTimeout(pendingCommandTimeout.current);
    return false;
  };

  /**
   * Applies general system fields from an accepted poll response.
   * @param {Object} data - Incoming poll response
   * @param {boolean} stale - Whether the response is stale
   * @returns {void}
   */
  const applySystemStatusData = (data, stale) => {
    if (data.timeReported) setTimeReported(new Date(data.timeReported));
    if (data.systemStatus && !stale) setSystemStatus(data.systemStatus);
    if (data.systemStatusDuration !== undefined) {
      setSystemStatusDuration(data.systemStatusDuration);
    }
    if (data.systemMessage) {
      setIsTestMode(data.systemMessage.toLowerCase().includes('test'));
    }
  };

  /**
   * Applies ControlByWeb connection, sensor, and relay fields from a poll response.
   * @param {Object|undefined} controlData - ControlByWeb status payload
   * @param {boolean} stale - Whether the response is stale
   * @returns {void}
   */
  const applyControlByWebData = (controlData, stale) => {
    if (!controlData) return;

    const { isConnected, connectionStatus: nextConnectionStatus } =
      deriveControlConnectionState(controlData, stale);
    if (nextConnectionStatus) setConnectionStatus(nextConnectionStatus);

    if (Array.isArray(controlData.sensors)) {
      setSensors(controlData.sensors.map(sensor => ({
        ...sensor,
        connection: isConnected ? sensor.connection : CONNECTION_STATUS.DISCONNECTED,
        type: getSensorType(sensor.id, sensor.type),
        name: getSensorName(sensor.id),
        icon: getSensorIcon(sensor.id, sensor.type),
      })));
    }

    if (Array.isArray(controlData.relays)) {
      setRelays(controlData.relays.map(relay => ({
        ...relay,
        connection: isConnected ? relay.connection : CONNECTION_STATUS.DISCONNECTED,
        name: getRelayName(relay.id),
        type: getRelayType(relay.id),
        icon: getRelayIcon(relay.id, getRelayType(relay.id)),
      })));
    }
  };

  /**
   * Applies external sensor fields from a poll response.
   * @param {Object|undefined} externals - External-device payload
   * @returns {void}
   */
  const applyExternalSensorData = (externals) => {
    if (!Array.isArray(externals?.sensors)) return;

    setExternalSensors(externals.sensors.map(sensor => ({
      ...sensor,
      type: getSensorType(sensor.id, sensor.type, 'external'),
      name: getSensorName(sensor.id),
      icon: getSensorIcon(sensor.id, sensor.type),
    })));
  };

  /**
   * Process incoming API data and update application state
   * @param {Object} data - HydroSystemStatus data from API
   */
  const processApiData = (data) => {
    console.log('Processing API data:', data);

    try {
      // Check for empty or default data
      if (!data) {
        console.warn('Received null/undefined data from API');
        console.warn('This indicates the API has not received real sensor updates yet');
        return;
      }

      if (shouldIgnorePendingCommandPoll(data)) return;

      setSystemData(data);
      setLastUpdate(Date.now());
      const stale = isStaleData(data.timeReported);
      applySystemStatusData(data, stale);
      applyControlByWebData(data.controlByWebStatus, stale);
      applyExternalSensorData(data.externals);
      dispatch({ type: SYSTEM_FLAGS_ACTION.DERIVE_FROM_API, payload: data });
      
    } catch (error) {
      console.error('Error processing API data:', error);
      setLastError(error);
    }
  };
    
  /**
   * Ensures the command API is initialized and its bearer token is current.
   * Must be called at the start of every command-dispatch function before constructing
   * a request, so the token refresh happens immediately before send.
   * @throws {Error} If commandApi has not been initialized via initializePolling
   */
  const ensureCommandApiReady = () => {
    if (!commandApi.current) {
      const error = new Error(ERROR_MESSAGES.COMMAND_API_NOT_INITIALIZED);
      setLastError(error);
      throw error;
    }

    // Update token before API call to support token refresh
    if (tokenGetter.current) {
      const currentToken = tokenGetter.current();
      if (currentToken) {
        commandApi.current.apiClient.authentications['BearerAuth'].accessToken = currentToken;
      }
    }
  };

  /**
   * Send a manual override command to the Fire Bridge
   * @param {string} state - The state to set (PRESOAK, ACTIVE, NOT_ACTIVE, PRIME, FULL_ACTIVE)
   * @param {string} duration - Optional duration in ISO 8601 format 'PT30M'
   * @param {string} delay - Optional delay in ISO 8601 format 'PT10S'
   * @param {string} fireControllerId - Fire Controller that receives the command
   * @returns {Promise<Object>} - Promise that resolves with the response data
   */
  const sendManualOverrideCommand = async (
    state,
    duration,
    delay,
    fireControllerId = selectedFireControllerId
  ) => {
    try {
      // Create the request object
      const request = new ManualOverrideCommandRequest(state);

      // Add optional parameters if provided
      if (duration) {
        request.duration = duration;
      }
      if (delay) {
        request.delay = delay;
      }

      // CRITICAL: Capture timestamp BEFORE sending
      const commandTimestamp = new Date();
      request.timeCommanded = commandTimestamp;

      // Store timestamp to filter poll data during processing window
      pendingCommandTimestamp.current = commandTimestamp;
      pendingCommandFireControllerId.current = fireControllerId;

      // Set timeout to clear lock if backend never responds
      clearTimeout(pendingCommandTimeout.current);
      pendingCommandTimeout.current = setTimeout(() => {
        console.warn('Command timeout - clearing lock after %d seconds', COMMAND_TIMEOUT_MS * MILISECONDS_TO_SECONDS);
        console.warn('Command:', state);
        console.warn('Command sent at:', commandTimestamp.toISOString());
        pendingCommandTimestamp.current = null;
        pendingCommandFireControllerId.current = null;
      }, COMMAND_TIMEOUT_MS);

      ensureCommandApiReady();

      // Use callback approach to avoid double requests
      const response = await new Promise((resolve, reject) => {
        commandApi.current.manualOverrideCommand(fireControllerId, request, (error, data) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      console.log(`${state} command sent at ${commandTimestamp.toISOString()}`, response);
      return response;
    } catch (error) {
      console.error(`Failed to send ${state} command:`, error);
      setLastError(error);

      // Clear lock on error
      pendingCommandTimestamp.current = null;
      pendingCommandFireControllerId.current = null;
      clearTimeout(pendingCommandTimeout.current);

      // Check for authentication errors (401 Unauthorized or 403 Forbidden)
      if (error.status === 401 || error.status === 403) {
        console.error('Authentication error on manual command - user session expired');

        keycloakService.logout();
      }

      throw error;
    }
  };

  /**
   * Send a self-targeted collaborative deactivate command to the Fire Bridge.
   * Targets the currently selected Fire Controller as both the command's origin and
   * destination, so the Fire Controller clears its own collaborative queue and ends
   * any active collaborative session.
   * @returns {Promise<Object>} - Promise that resolves with the response data
   */
  const sendCollaborativeDeactivateCommand = async () => {
    ensureCommandApiReady();

    try {
      // Create the request object. validityDuration is a placeholder required by the
      // generated model but is overwritten server-side and has no effect.
      const request = new CollaborativeCommand(
        CollaborativeCommand.StateEnum.COLLAB_NOT_ACTIVE,
        selectedFireControllerId,
        DEACTIVATION_DURATION_SHORT_PRESOAK
      );

      // CRITICAL: Capture timestamp BEFORE sending
      const commandTimestamp = new Date();
      request.timeCommanded = commandTimestamp;

      // Store timestamp to filter poll data during processing window
      pendingCommandTimestamp.current = commandTimestamp;
      pendingCommandFireControllerId.current = selectedFireControllerId;

      // Set timeout to clear lock if backend never responds
      clearTimeout(pendingCommandTimeout.current);
      pendingCommandTimeout.current = setTimeout(() => {
        console.warn('Command timeout - clearing lock after %d seconds', COMMAND_TIMEOUT_MS * MILISECONDS_TO_SECONDS);
        console.warn('Command:', CollaborativeCommand.StateEnum.COLLAB_NOT_ACTIVE);
        console.warn('Command sent at:', commandTimestamp.toISOString());
        pendingCommandTimestamp.current = null;
        pendingCommandFireControllerId.current = null;
      }, COMMAND_TIMEOUT_MS);

      // Use callback approach to avoid double requests
      const response = await new Promise((resolve, reject) => {
        commandApi.current.postCollaborativeCommand(selectedFireControllerId, request, (error, data) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      console.log(`Collaborative deactivate command sent at ${commandTimestamp.toISOString()}`, response);
      return response;
    } catch (error) {
      console.error('Failed to send collaborative deactivate command:', error);
      setLastError(error);

      // Clear lock on error
      pendingCommandTimestamp.current = null;
      pendingCommandFireControllerId.current = null;
      clearTimeout(pendingCommandTimeout.current);

      // Check for authentication errors (401 Unauthorized or 403 Forbidden)
      if (error.status === 401 || error.status === 403) {
        console.error('Authentication error on collaborative deactivate command - user session expired');

        keycloakService.logout();
      }

      throw error;
    }
  };

  /**
   * Get sensor type based on ID and API type
   * @param {string} sensorId - Sensor ID
   * @param {string} apiType - Type from API
   * @param {string} category - Category (internal/external)
   * @returns {string} Sensor type for UI
   */
  const getSensorType = (sensorId, apiType, category = 'internal') => {
    const apiStringTypes = {
      'Ember Sensor': 'ember',
      'Smoke Sensor': 'smoke', 
      'IR Sensor': 'ir',
      'IRCameraSensor': 'ir',
      'Weather Station - Temp': 'temperature',
    };
  
    return apiStringTypes[sensorId] || apiType || category;
  };

  /**
   * Get React icon component for sensor based on type
   * @param {string} sensorId - Sensor ID
   * @param {string} sensorType - Sensor type
   * @returns {JSX.Element} React icon component
   */
  const getSensorIcon = (sensorId, sensorType) => {
    const iconMap = {
      'Ember Sensor': <FaFire />,
      'Smoke Sensor': <FaSmog />,
      'IR Sensor': <FaSearch />,
      'IRCameraSensor': <FaSearch />,
      'Weather Station - Temp': <FaThermometerHalf />,
      'ember': <FaFire />,
      'smoke': <FaSmog />,
      'ir': <FaSearch />,
      'signal': <FaSignal />,
      'temperature': <FaThermometerHalf />,
      'pressure': <FaTachometerAlt />,
      'flow': <FaWater />,
      'water': <FaTint />,
    };
    
    return iconMap[sensorId] || iconMap[sensorType] || <FaSearch />;
  };


  /**
   * Get relay type based on ID
   * @param {string} relayId - Relay ID
   * @returns {string} Relay type
   */
  const getRelayType = (relayId) => {
    const apiStringTypes = {
      'Main Water Valve Relay': 'valve',
      'Primary Pump Relay': 'pump',

    };
    
    return apiStringTypes[relayId] || 'relay';
  };

  /**
   * Get React icon component for relay based on type
   * @param {string} relayId - Relay ID
   * @param {string} relayType - Relay type
   * @returns {JSX.Element} React icon component
   */
  const getRelayIcon = (relayId, relayType) => {
    const iconMap = {
      'Main Water Valve Relay': <FaTint />,
      'Primary Pump Relay': <MdOutlinePower />,
    };
    
    return iconMap[relayId] || iconMap[relayType] || <FaCog />;
  };

  /**
   * Initialize polling with the given IP address
   * @param {string} ip - Fire Bridge IP address
   * @param {Function} getTokenFn - Function that returns the current authentication token.
   *                                Called before each API request to support dynamic token refresh.
   *                                For static tokens, use: () => 'your-static-token'
   */
  const initializePolling = (ip, getTokenFn) => {
    try {
      const baseUrl = `${ip}`;
      pollingService.initialize(baseUrl, getTokenFn);
      ipAddress.current = ip;
      tokenGetter.current = getTokenFn;

      const apiClient = new ApiClient(baseUrl);

      // Set initial token for commandApi
      if (getTokenFn) {
        const currentToken = getTokenFn();
        if (currentToken) {
          apiClient.authentications['BearerAuth'].accessToken = currentToken;
        }
      }

      commandApi.current = new CommandApi(apiClient);
      
      const unsubscribeData = pollingService.onData(processApiData);
      
      const unsubscribeError = pollingService.onError((error, status) => {
        console.error('Polling error:', error);
        setLastError(error);
        setConnectionStatus(status);
      });
      
      pollingService.startPolling();
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);
      
      isInitialized.current = true;
      
      return () => {
        unsubscribeData();
        unsubscribeError();
        pollingService.stopPolling();
        isInitialized.current = false;
      };
    } catch (error) {
      console.error('Failed to initialize polling:', error);
      setLastError(error);
      setConnectionStatus(CONNECTION_STATUS.ERROR);
      throw error;
    }
  };

  /**
   * Stop polling and cleanup
   */
  const stopPolling = () => {
    pollingService.stopPolling();
    setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    isInitialized.current = false;
  };

  /**
   * Update the IP address and restart polling
   * @param {string} ip - New IP address
   */
  const updateIpAddress = (ip) => {
    if (ipAddress.current === ip) {
      return;
    }
    
    if (isInitialized.current) {
      stopPolling();
    }
    
    initializePolling(ip);
  };

  /** Update connection status from polling service and check for staleness */
  useEffect(() => {
    const interval = setInterval(() => {
      // Only update connection status from polling service if we don't have a controlByWebStatus
      // or if the polling service reports an error
      const pollingStatus = pollingService.getConnectionStatus();

      if (pollingStatus === CONNECTION_STATUS.ERROR || !systemData?.controlByWebStatus?.connection) {
        setConnectionStatus(pollingStatus);
      }

      // Staleness check: if timeReported exceeds threshold, mark everything disconnected
      if (isStaleData(timeReported) && connectionStatus !== CONNECTION_STATUS.DISCONNECTED) {
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        setSensors(prev => prev.map(s => ({ ...s, connection: CONNECTION_STATUS.DISCONNECTED })));
        setRelays(prev => prev.map(r => ({ ...r, connection: CONNECTION_STATUS.DISCONNECTED })));
        setExternalSensors(prev => prev.map(s => ({ ...s, connection: CONNECTION_STATUS.DISCONNECTED })));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [systemData, timeReported, connectionStatus]);

  const initiatePresoak = async () => {
    console.log('Initiating presoak...');

    // Capture current status before optimistic update
    previousSystemStatus.current = systemStatus;

    // Optimistic UI update - show immediate feedback
    setSystemStatus({ systemMode: SYSTEM_MODE.MANUAL, sharedMode: SHARED_MODE.OFF, specialMode: SPECIAL_MODE.PRESOAKING });
    dispatch({ type: SYSTEM_FLAGS_ACTION.PRESOAK_START });

    try {
      await sendManualOverrideCommand('PRESOAK', 'PT15M', 'PT0S');
      console.log('Presoak command sent successfully');
      // Note: Real status will be updated by next polling cycle
    } catch (error) {
      console.error('Failed to send presoak command:', error);
      // Revert optimistic update on failure to previous status
      setSystemStatus(previousSystemStatus.current);
      dispatch({ type: SYSTEM_FLAGS_ACTION.PRESOAK_STOP });
      throw error;
    }
  };

  /**
   * Initiates full activation of the fire defense system
   * @returns {void}
   */
  const initiateActivation = async () => {
    console.log('Activating system...');

    // Capture current status before optimistic update
    previousSystemStatus.current = systemStatus;

    // Optimistic UI update - show immediate feedback
    setSystemStatus({ systemMode: SYSTEM_MODE.MANUAL, sharedMode: SHARED_MODE.ACTIVATING, specialMode: SPECIAL_MODE.NONE });
    dispatch({ type: SYSTEM_FLAGS_ACTION.ACTIVATION_START });

    try {
      await sendManualOverrideCommand('ACTIVE', 'PT876000H', 'PT0S');
      console.log('Activation command sent successfully');
      // Note: Real status will be updated by next polling cycle
    } catch (error) {
      console.error('Failed to send activation command:', error);
      // Revert optimistic update on failure to previous status
      setSystemStatus(previousSystemStatus.current);
      throw error;
    }
  };

  /**
   * Stops the presoak process
   * @returns {Promise<void>}
   */
  const stopPresoak = async () => {
    console.log('Stopping presoak...');

    // Capture current status before optimistic update
    previousSystemStatus.current = systemStatus;

    // Optimistic UI update
    setSystemStatus({ systemMode: SYSTEM_MODE.MANUAL, sharedMode: SHARED_MODE.DEACTIVATING, specialMode: SPECIAL_MODE.PRESOAKING });
    // Note: isPresoaking will be cleared by deriveSystemState when status becomes INACTIVE

    try {
      const duration = getDeactivationDuration(sensors);
      await sendManualOverrideCommand('NOT_ACTIVE', duration, 'PT0S');
      console.log('Stop presoak command sent successfully');
      dispatch({ type: SYSTEM_FLAGS_ACTION.PRESOAK_STOP });
    } catch (error) {
      console.error('Failed to send stop presoak command:', error);
      setSystemStatus(previousSystemStatus.current);
      throw error;
    }
  };

  /**
   * Stops an in-progress collaborative defense sequence and returns to deactivating state.
   * Uses a zero-second deactivation duration so the autonomous system can re-engage immediately.
   * @returns {Promise<void>}
   */
  const stopCollaboration = async () => {
    console.log('Stopping collaboration...');

    // Capture current status before optimistic update
    previousSystemStatus.current = systemStatus;

    // Optimistic UI update — reflect deactivating state immediately
    setSystemStatus({ systemMode: SYSTEM_MODE.MANUAL, sharedMode: SHARED_MODE.DEACTIVATING, specialMode: SPECIAL_MODE.COLLABORATIVE_SOAK });
    dispatch({ type: SYSTEM_FLAGS_ACTION.COLLABORATION_STOP });

    try {
      // Sends a real, self-targeted CollaborativeCommand DEACTIVATE (fireControllerId
      // equals this FC's own id). This relies on the Fire Controller's self-targeted
      // DEACTIVATE handling (CollaborativeCommandIntent.processDeactivateCommand()) to
      // clear its collaborative queue and end the active session, entering a genuine cooldown.
      await sendCollaborativeDeactivateCommand();
      console.log('Stop collaboration command sent successfully');
    } catch (error) {
      console.error('Failed to send stop collaboration command:', error);
      setSystemStatus(previousSystemStatus.current);
      throw error;
    }
  };

  /**
   * Stops the active fire defense system and returns to ready status with cooldown
   * @returns {Promise<void>}
   */
  const stopActivation = async () => {
    console.log('Stopping activation...');

    // Capture current status before optimistic update
    previousSystemStatus.current = systemStatus;

    // Optimistic UI update
    setSystemStatus({ systemMode: SYSTEM_MODE.MANUAL, sharedMode: SHARED_MODE.DEACTIVATING, specialMode: SPECIAL_MODE.NONE });

    try {
      const duration = getDeactivationDuration(sensors, DEACTIVATION_TYPE.ACTIVATION);
      await sendManualOverrideCommand('NOT_ACTIVE', duration, 'PT0S');
      console.log('Stop activation command sent successfully');
    } catch (error) {
      console.error('Failed to send stop activation command:', error);
      setSystemStatus(previousSystemStatus.current);
      throw error;
    }
  };

  /**
   * Resets a Fire Controller without optimistically changing the selected controller's state.
   * @param {string} fireControllerId - Fire Controller that receives the reset command
   * @returns {Promise<Object>} Promise that resolves with the command response
   */
  const manualReset = async (fireControllerId) => {
    return sendManualOverrideCommand(
      MANUAL_RESET_STATE,
      MANUAL_RESET_DURATION,
      MANUAL_RESET_DELAY,
      fireControllerId
    );
  };

  /**
   * Toggles the system test mode on or off
   * @returns {void}
   */
  const toggleTestMode = () => {
    setIsTestMode(prevState => !prevState);
  };

  /**Force a manual refresh of sensor data*/
  const refreshData = async () => {
    try {
      await pollingService.manualPoll();
    } catch (error) {
      console.error('Failed to refresh data:', error);
      setLastError(error);
    }
  };

  /**
   * Maps internal status values to user-friendly display text
   * @param {string} status - The internal status value (e.g., 'standby', 'inactive', 'active')
   * @returns {string} User-friendly status text
   */
  const getDisplayStatus = (status) => {
    if (!status) return status;

    const statusLower = status.toLowerCase();

    if (statusLower === 'standby') return 'Ready';
    if (statusLower === 'inactive') return 'Error';
    if (statusLower === 'active') return 'Alert';

    // Return original status if no mapping exists
    return status;
  };

  /**
   * Gets the CSS color class for a status text
   * @param {string} status - The internal status value (e.g., 'standby', 'inactive', 'active')
   * @returns {string} CSS class name for status color
   */
  const getStatusColorClass = (status) => {
    if (!status) return '';

    const statusLower = status.toLowerCase();

    if (statusLower === 'standby') return CSS_STATUS_CLASS.READY;
    if (statusLower === 'inactive') return CSS_STATUS_CLASS.ERROR;
    if (statusLower === 'active') return CSS_STATUS_CLASS.ALERT;

    // Return empty string if no mapping exists
    return '';
  };

  /**
   * Returns a status string for a given sensor type based on the current sensor data.
   * @param {string} sensorType - The type of sensor (e.g., 'ember', 'smoke', 'water', 'signal').
   * @returns {string} The status of the sensor from HEALTH_STATUS enum.
   */
  const getSensorStatusFromData = (sensorType) => {
    const allSensors = [...(sensors || []), ...(externalSensors || [])];
    const sensorId = SENSOR_ID_MAP[sensorType];
    const sensor = allSensors.find(s => s.id === sensorId);
    return deriveSensorHealth(sensorType, sensor);
  };

  // Sync selected Fire Controller into polling service; immediately refresh on change
  useEffect(() => {
    // Clear stale state from the previous controller before loading new data
    setSystemData(null);
    setSystemStatus(null);
    setSensors([]);
    setRelays([]);
    setExternalSensors([]);
    setTimeReported(null);
    setSystemStatusDuration(null);
    dispatch({ type: SYSTEM_FLAGS_ACTION.RESET });
    setIsTestMode(false);
    setLastUpdate(null);
    clearTimeout(pendingCommandTimeout.current);
    pendingCommandTimeout.current = null;
    pendingCommandTimestamp.current = null;
    pendingCommandFireControllerId.current = null;

    pollingService.setFireControllerId(selectedFireControllerId);

    if (selectedFireControllerId && isInitialized.current) {
      setConnectionStatus(CONNECTION_STATUS.CONNECTING);
      pollingService.manualPoll();
    } else {
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    }
  }, [selectedFireControllerId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pendingCommandTimeout.current) {
        clearTimeout(pendingCommandTimeout.current);
      }
    };
  }, []);

  return (
    <SystemContext.Provider
      value={{
        systemStatus,
        isPresoaking,
        isActive,
        isCollaborative,
        activeTime,
        isTestMode,

        systemData,
        sensors,
        relays,
        externalSensors,

        connectionStatus,
        lastError,
        lastUpdate,
        timeReported,

        initiatePresoak,
        initiateActivation,
        stopPresoak,
        stopCollaboration,
        stopActivation,
        manualReset,
        toggleTestMode,
        refreshData,

        initializePolling,
        stopPolling,
        updateIpAddress,

        getSensorStatusFromData,
        getDisplayStatus,
        getStatusColorClass,
        getSensorName,
        getRelayName,

        systemStatusDuration,

        isPolling: isInitialized.current,
        currentIpAddress: ipAddress.current,
      }}
    >
      {children}
    </SystemContext.Provider>
  );
};

/**
 * Custom hook to use the system context
 * @returns {Object} The system context object containing:
 * - systemStatus: Current system status string
 * - isPresoaking: Boolean indicating if system is in presoak mode
 * - isActive: Boolean indicating if system is fully active
 * - activeTime: Number of minutes the system will be active(default: 30)
 * - isTestMode: Boolean indicating if system is in test mode
 * - relays: Array of relay objects with current states
 * - connectionStatus: Current connection status string
 * - getAllRelays: Function to get relays with fallback to mock data
 * - setConnectionStatus: Function to update connection status
 * - initiatePresoak: Function to start presoaking
 * - initiateActivation: Function to start full activation
 * - stopActivationAndPresoak: Function to stop activation and presoak
 * - toggleTestMode: Function to toggle test mode
 * - getSensorStatusFromData: Function to get sensor status by type
 */
export const useSystem = () => useContext(SystemContext);

export default SystemContext;
